import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Label } from '@/components/ui/label'
import { getJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'

type SortKey = 'name' | 'active_member_count' | 'created_at'
type SortDirection = 'asc' | 'desc'

export interface ChapterRow {
  id: number
  name: string
  meeting_location: string
  active_member_count: number
  tagged_contact_count: number
  public_visibility: boolean
  archived: boolean
  created_at: string
}

interface ChaptersResponse {
  chapters: ChapterRow[]
}

export function ChaptersListPage({
  onOpenChapter,
}: {
  onOpenChapter?: (chapter: ChapterRow) => void
}) {
  const [search, setSearch] = React.useState('')
  const [visibility, setVisibility] = React.useState<ReadonlySet<string>>(new Set())
  const [includeArchived, setIncludeArchived] = React.useState(false)
  const [sortKey, setSortKey] = React.useState<SortKey>('name')
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc')
  const filter = includeArchived ? 'all' : 'active'
  const visibilityValue = visibility.size === 1 ? [...visibility][0] : undefined
  const query = useQuery({
    queryKey: ['chapters', search, filter, visibilityValue, sortKey, sortDirection],
    queryFn: () =>
      getJSON<ChaptersResponse>(
        chapterPath({ search, filter, visibility: visibilityValue, sortKey, sortDirection }),
      ),
    retry: false,
  })

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const columns = chapterColumns(sortKey, sortDirection, toggleSort)

  return (
    <ListPage
      title="Chapters"
      description="Local groups inside the association."
      search={{ value: search, onChange: setSearch, placeholder: 'Search chapters' }}
      toolbar={
        <div className="flex items-center gap-3">
          <FilterDropdown
            label="Visibility"
            emptyLabel="Visibility"
            options={[
              { value: 'public', label: 'Public' },
              { value: 'hidden', label: 'Hidden' },
            ]}
            selected={visibility}
            onChange={setVisibility}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-archived"
              checked={includeArchived}
              onCheckedChange={(checked) => setIncludeArchived(checked === true)}
            />
            <Label htmlFor="include-archived" className="text-muted-foreground">
              Include archived
            </Label>
          </div>
        </div>
      }
      table={{
        rows: query.data?.chapters ?? [],
        columns,
        getRowId: (chapter) => chapter.id,
        getRowLabel: (chapter) => chapter.name,
        onRowOpen: onOpenChapter,
        state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
        emptyTitle: 'No chapters found',
      }}
    />
  )
}

function chapterColumns(
  sortKey: SortKey,
  sortDirection: SortDirection,
  toggleSort: (key: SortKey) => void,
): ListPageColumn<ChapterRow>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      cell: (chapter) => (
        <>
          {chapter.name}
          {chapter.archived && (
            <Badge variant="neutral" className="ml-2">
              Archived
            </Badge>
          )}
        </>
      ),
      sort: sortFor('name', sortKey, sortDirection, toggleSort),
    },
    {
      id: 'meeting_location',
      header: 'Meeting location',
      cell: (chapter) => (
        <span className="text-muted-foreground">{chapter.meeting_location || '—'}</span>
      ),
    },
    {
      id: 'members',
      header: 'Members',
      cell: (chapter) => (
        <span>
          <span>{chapter.active_member_count} active</span>
          <span className="text-muted-foreground"> · {chapter.tagged_contact_count} tagged</span>
        </span>
      ),
      sort: sortFor('active_member_count', sortKey, sortDirection, toggleSort),
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: (chapter) => (
        <span className="text-muted-foreground">{displayDateTime(chapter.created_at)}</span>
      ),
      sort: sortFor('created_at', sortKey, sortDirection, toggleSort),
    },
    {
      id: 'visibility',
      header: 'Public page',
      cell: (chapter) => (
        <Badge variant={chapter.public_visibility ? 'info' : 'neutral'}>
          {chapter.public_visibility ? 'Listed' : 'Not listed'}
        </Badge>
      ),
    },
  ]
}

function sortFor(
  key: SortKey,
  active: SortKey,
  direction: SortDirection,
  onToggle: (key: SortKey) => void,
) {
  const sortDirection = active === key && direction === 'desc' ? 'descending' : 'ascending'
  return {
    active: active === key,
    direction: sortDirection as 'ascending' | 'descending',
    onToggle: () => onToggle(key),
  }
}

function chapterPath({
  search,
  filter,
  visibility,
  sortKey,
  sortDirection,
}: {
  search: string
  filter: string
  visibility?: string
  sortKey: SortKey
  sortDirection: SortDirection
}) {
  const params = new URLSearchParams({ filter, sort: sortKey, direction: sortDirection })
  if (search) params.set('search', search)
  if (visibility) params.set('visibility', visibility)
  return `/api/v1/chapters?${params.toString()}`
}
